/**
 * Saving a capsule as a draft, and deleting one.
 *
 * A draft is a real record on the creator's PDS with `status: 'draft'`, and it
 * creates **no post**: nothing shows up in any feed, and the only way back to it is
 * the drafts list. That is what makes it the thing that keeps unfinished work
 * safe — the device-local autosave copy protects against a closed tab, and this
 * protects against losing the phone.
 *
 * Deliberately *not* here: the cover film. Publishing uploads it as the post's
 * video embed, and a draft has no post, so a draft carries the editable capsule and
 * not the file. The creator is told that where they meet it rather than discovering
 * it later.
 */
import {type AtpAgent} from '@atproto/api'
import {AtUri} from '@atproto/api'

import {type Slide, SLIDE_TYPE_META} from '#/types/capsule'

export const CAPSULE_COLLECTION = 'app.bsky.feed.capsule'

export type SavedCapsuleRef = {
  uri: string
  cid: string
  /**
   * The record's own `createdAt`. The studio keeps this so that publishing a
   * capsule that came from a draft can convert the record in place without
   * backdating it to the moment it was published.
   */
  createdAt: string
}

/** How many slides in a capsule are exercises, as the record reports it. */
export function countExercises(slides: readonly Slide[]): number {
  return slides.filter(slide => SLIDE_TYPE_META[slide.type]?.isExercise).length
}

/**
 * Saves an unfinished capsule as a draft record.
 *
 * Saving a draft that came from a draft *updates* that record rather than creating
 * another one. Without that, every save during one editing session would leave a
 * separate half-finished capsule behind, and the creator's own list would be made
 * useless by the tool that was supposed to protect their work.
 */
export async function saveCapsuleDraft({
  agent,
  did,
  title,
  description,
  themeId,
  backgroundMusicId,
  htmlThemeUrl,
  slides,
  draftUri,
  createdAt: originalCreatedAt,
}: {
  agent: AtpAgent
  did: string
  title: string
  description: string
  themeId: string
  /** The creator's music choice, in the record's spelling (`'none'` for silence). */
  backgroundMusicId?: string
  htmlThemeUrl?: string
  /** The content slides, i.e. everything after the cover. */
  slides: Slide[]
  /** The record this capsule already is, when it was opened from a draft. */
  draftUri?: string | null
  /** The record's own `createdAt`, carried across an in-place update. */
  createdAt?: string | null
}): Promise<SavedCapsuleRef> {
  const now = new Date().toISOString()
  const createdAt = originalCreatedAt ?? now
  const record = {
    $type: CAPSULE_COLLECTION,
    title,
    description,
    // The whole point: a draft is not in any feed.
    status: 'draft' as const,
    themeId: themeId || 'default',
    backgroundMusicId,
    behaviorSettings: htmlThemeUrl ? {htmlThemeUrl} : undefined,
    slides,
    slideCount: countExercises(slides),
    createdAt,
  }

  if (draftUri) {
    const rkey = new AtUri(draftUri).rkey
    const res = await agent.com.atproto.repo.putRecord({
      repo: did,
      collection: CAPSULE_COLLECTION,
      rkey,
      record,
    })
    return {uri: res.data.uri, cid: res.data.cid ?? '', createdAt}
  }

  const res = await agent.com.atproto.repo.createRecord({
    repo: did,
    collection: CAPSULE_COLLECTION,
    record,
  })
  return {uri: res.data.uri, cid: res.data.cid ?? '', createdAt}
}

/**
 * Deletes a draft.
 *
 * A hard delete rather than a tombstone, because the creator asked for it to be
 * gone — and the drafts list already asks them to confirm, so a soft delete would
 * only mean the record came back to a list that had promised it would not.
 */
export async function deleteCapsuleDraft({
  agent,
  did,
  uri,
}: {
  agent: AtpAgent
  did: string
  uri: string
}): Promise<void> {
  const atUri = new AtUri(uri)
  await agent.com.atproto.repo.deleteRecord({
    repo: did,
    collection: atUri.collection || CAPSULE_COLLECTION,
    rkey: atUri.rkey,
  })
}
